import { Controller } from "@hotwired/stimulus";
import { Turbo } from "@hotwired/turbo-rails";
import { showConfirmation } from "../helpers/confirmation_dialog";
import { loadFilePond } from "../models/filepond_loader";

// One coordinator avoids multiple prompts when a page contains several forms.
const trackedForms = new Set();
const guardedModals = new Map();
let currentEntry;
let restoreHistoryDone = null;
let confirmationOpen = false;
let globalListenersAttached = false;
const confirmedModalHides = new WeakSet();

function rememberEntry() {
  currentEntry = { state: history.state, url: location.href };
}

function formsWithUnsavedChanges(container = document, includeContainingForm = false) {
  return [...trackedForms].filter(form => {
    const isFormInsideContainer = container.contains(form.element);
    const isContainerInsideForm = includeContainingForm && form.element.contains(container);
    return (isFormInsideContainer || isContainerInsideForm) && form.isDirty();
  });
}

async function confirmLeaving(container = document, includeContainingForm = false) {
  const changedForms = formsWithUnsavedChanges(container, includeContainingForm);
  if (!changedForms.length) return true;
  if (confirmationOpen) return false;
  confirmationOpen = true;
  try {
    const confirmed = await showConfirmation({
      title: changedForms[0].titleValue,
      text: changedForms[0].messageValue,
      confirmButtonText: "Discard",
      cancelButtonText: "Keep Editing",
    });
    if (!confirmed || !changedForms.some(form => trackedForms.has(form))) return false;
    for (const form of changedForms) {
      if (!trackedForms.has(form)) continue;
      await form.discard();
      form.allowNavigation = true;
    }
    return true;
  } finally {
    confirmationOpen = false;
  }
}

async function beforeVisit(event) {
  if (!formsWithUnsavedChanges().length) return;
  event.preventDefault();
  if (await confirmLeaving()) {
    Turbo.visit(event.detail.url, { action: event.detail.action || "advance" });
  }
}

async function nativeLinkClick(event) {
  if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
  const link = event.target.closest("a[href]");
  if (!link || link.hasAttribute("download") || (link.target && link.target !== "_self") || link.dataset.turboMethod) return;
  const url = new URL(link.href, location.href);
  if (!["http:", "https:"].includes(url.protocol)) return;
  if (url.pathname === location.pathname && url.search === location.search && url.hash) return;
  if (url.origin === location.origin && link.closest("[data-turbo]")?.dataset.turbo !== "false") return;
  if (!formsWithUnsavedChanges().length) return;
  event.preventDefault();
  event.stopImmediatePropagation();
  if (await confirmLeaving()) location.assign(url.href);
}

function beforeUnload(event) {
  if (!formsWithUnsavedChanges().length) return;
  event.preventDefault();
  event.returnValue = "";
}

async function popState(event) {
  if (restoreHistoryDone) {
    event.stopImmediatePropagation();
    const done = restoreHistoryDone;
    restoreHistoryDone = null;
    done();
    return;
  }
  if (!formsWithUnsavedChanges().length) return;

  // Restore the current entry before asking. Turbo must not replace the draft
  // while SweetAlert is open; replay the history traversal only after approval.
  event.stopImmediatePropagation();
  const destination = location.href;
  const from = currentEntry?.state?.turbo?.restorationIndex;
  const to = event.state?.turbo?.restorationIndex;
  const delta = Number.isInteger(from) && Number.isInteger(to) ? to - from : 0;
  if (delta) {
    await new Promise(resolve => {
      restoreHistoryDone = resolve;
      history.go(-delta);
    });
  } else if (currentEntry) {
    history.replaceState(currentEntry.state, "", currentEntry.url);
  }
  if (await confirmLeaving()) {
    if (delta) history.go(delta);
    else Turbo.visit(destination, { action: "replace" });
  }
}

async function beforeFrameRender(event) {
  if (!formsWithUnsavedChanges(event.target).length) return;
  event.preventDefault();
  if (await confirmLeaving(event.target)) event.detail.resume();
}

function hasUncommittedModalChanges(modal, guard) {
  if (guard?.hasUncommittedModalChanges()) return true;
  if (modal.dataset.unsavedChangesModalGuardValue !== "true") return false;
  if (modal.dataset.unsavedChangesModalPending === "true") return true;

  const dateInput = modal.querySelector("[data-flatpickr-type-value=\"datetime\"]");
  if (
    dateInput?.dataset.pendingScheduledAt ||
    (dateInput &&
      dateInput.dataset.unsavedChangesOriginalValue !== undefined &&
      dateInput.value !== dateInput.dataset.unsavedChangesOriginalValue)
  ) {
    return true;
  }

  const locationInput = modal.querySelector("[data-places-target~=\"address\"]");
  return Boolean(
    locationInput &&
    locationInput.value !== locationInput.dataset.originalValue
  );
}

function discardUncommittedModalChanges(modal, guard) {
  if (guard) {
    guard.discardUncommittedModalChanges();
    return;
  }

  delete modal.dataset.unsavedChangesModalPending;
  const dateInput = modal.querySelector("[data-flatpickr-type-value=\"datetime\"]");
  if (dateInput) {
    delete dateInput.dataset.pendingScheduledAt;
    delete dateInput.dataset.pendingScheduleDisplay;
  }

  const locationInput = modal.querySelector("[data-places-target~=\"address\"]");
  if (locationInput) {
    locationInput.value = locationInput.dataset.originalValue || "";
    delete locationInput.dataset.selectedValue;
    locationInput.setCustomValidity("");
  }
}

async function modalHide(event) {
  if (confirmedModalHides.has(event.target)) {
    confirmedModalHides.delete(event.target);
    for (const form of trackedForms) {
      if (event.target.contains(form.element)) form.allowNavigation = false;
    }
    return;
  }
  if (event.target.dataset.unsavedChangesSkipModalConfirmation === "true") {
    delete event.target.dataset.unsavedChangesSkipModalConfirmation;
    return;
  }

  // Modal selections are temporary until the user confirms them in the modal.
  const modalGuard = guardedModals.get(event.target);
  // Closing an inner composer dialog must not discard the form that contains it.
  const changedForms = modalGuard ? [] : formsWithUnsavedChanges(event.target, true);
  const hasModalChanges = hasUncommittedModalChanges(event.target, modalGuard);
  if (!changedForms.length && !hasModalChanges) return;

  event.preventDefault();
  event.stopImmediatePropagation();

  if (changedForms.length) {
    if (await confirmLeaving(event.target, true)) {
      confirmedModalHides.add(event.target);
      globalThis.bootstrap.Modal.getInstance(event.target)?.hide();
    }
    return;
  }

  if (hasModalChanges) {
    if (confirmationOpen) return;
    confirmationOpen = true;
    try {
      const confirmed = await showConfirmation({
        title: modalGuard?.modalTitleValue || "Unsaved changes",
        text: modalGuard?.modalMessageValue ||
          "You have unsaved changes. Do you still want to close this modal?",
        confirmButtonText: "Close",
        cancelButtonText: "Keep Editing"
      });
      if (!confirmed || !event.target.isConnected) return;

      discardUncommittedModalChanges(event.target, modalGuard);
      confirmedModalHides.add(event.target);
      globalThis.bootstrap.Modal.getInstance(event.target)?.hide();
    } finally {
      confirmationOpen = false;
    }
    return;
  }
}

function addGlobalListeners() {
  if (globalListenersAttached) return;
  for (const [target, name, handler, capture] of listeners) {
    target.addEventListener(name, handler, capture);
  }
  globalListenersAttached = true;
}

function removeGlobalListeners() {
  if (!globalListenersAttached) return;
  for (const [target, name, handler, capture] of listeners) {
    target.removeEventListener(name, handler, capture);
  }
  globalListenersAttached = false;
  restoreHistoryDone?.();
  restoreHistoryDone = null;
}

function removeGlobalListenersWhenUnused() {
  if (trackedForms.size || guardedModals.size) return;
  removeGlobalListeners();
}

function pageShow() {
  rememberEntry();
  for (const form of trackedForms) form.allowNavigation = false;
}

const listeners = [
  [document, "turbo:before-visit", beforeVisit],
  [document, "click", nativeLinkClick, true],
  [document, "turbo:before-frame-render", beforeFrameRender],
  [document, "hide.bs.modal", modalHide, true],
  [document, "turbo:load", pageShow],
  [globalThis, "pageshow", pageShow],
  [globalThis, "beforeunload", beforeUnload],
  [globalThis, "popstate", popState, true],
];

export default class UnsavedChangesController extends Controller {
  static values = {
    title: { type: String, default: "Discard changes?" },
    message: { type: String, default: "You have unsaved changes. Leave without saving?" },
    cleanOnSubmit: { type: Boolean, default: true },
    // Modal guards track uncommitted date, location, and GIF selections.
    modalGuard: { type: Boolean, default: false },
    modalTitle: { type: String, default: "Unsaved changes" },
    modalMessage: {
      type: String,
      default: "You have unsaved changes. Do you still want to close this modal?"
    },
    // The GIF picker is shared with messages; only guard it for post forms.
    postForm: { type: Boolean, default: false },
  };

  connect() {
    if (this.isGuardedModal()) {
      this.isModalGuard = true;
      this.onModalFieldChange = this.markModalChanged.bind(this);
      for (const eventName of ["input", "change", "location-selected"]) {
        this.element.addEventListener(eventName, this.onModalFieldChange);
      }
      if (!globalListenersAttached) rememberEntry();
      guardedModals.set(this.element, this);
      addGlobalListeners();
      return;
    }

    this.allowNavigation = false;
    this.submitting = false;
    this.initialState = this.element.dataset.unsavedChangesBaseline || this.serialize();
    this.element.dataset.unsavedChangesBaseline = this.initialState;
    this.onEdit = () => {
      this.allowNavigation = false;
      rememberEntry();
    };
    this.onSubmitStart = () => { this.submitting = true; };
    this.onSubmitEnd = event => {
      this.submitting = false;
      if (event.detail.success && this.cleanOnSubmitValue) this.markClean();
    };
    this.onClean = () => this.markClean();
    this.bindings = [
      ["input", this.onEdit], ["change", this.onEdit],
      ["turbo:submit-start", this.onSubmitStart],
      ["turbo:submit-end", this.onSubmitEnd],
      ["unsaved-changes:clean", this.onClean],
    ];
    for (const [name, handler] of this.bindings) this.element.addEventListener(name, handler);
    if (!globalListenersAttached) rememberEntry();
    trackedForms.add(this);
    addGlobalListeners();
  }

  disconnect() {
    if (this.isModalGuard) {
      for (const eventName of ["input", "change", "location-selected"]) {
        this.element.removeEventListener(eventName, this.onModalFieldChange);
      }
      guardedModals.delete(this.element);
      removeGlobalListenersWhenUnused();
      return;
    }

    for (const [name, handler] of this.bindings) this.element.removeEventListener(name, handler);
    trackedForms.delete(this);
    removeGlobalListenersWhenUnused();
  }

  isGuardedModal() {
    if (!this.element.matches(".modal")) return false;

    return this.modalGuardValue ||
      this.element.id === "addGifModal" ||
      Boolean(this.element.querySelector(
        "[data-flatpickr-type-value=\"datetime\"], [data-places-target~=\"address\"]"
      ));
  }

  markModalChanged(event) {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || !input.closest(".modal")) return;

    let originalValue;
    if (input.dataset.flatpickrTypeValue === "datetime") {
      originalValue = input.dataset.unsavedChangesOriginalValue;
    } else if (input.matches("[data-places-target~=\"address\"]")) {
      originalValue = input.dataset.originalValue;
    } else {
      return;
    }

    const isChanged =
      Boolean(input.dataset.pendingScheduledAt) || input.value !== originalValue;

    this.element.dataset.unsavedChangesModalPending = String(isChanged);
  }

  hasUncommittedModalChanges() {
    if (this.hasPostFormValue && !this.postFormValue) return false;

    if (this.element.dataset.unsavedChangesModalPending === "true") return true;

    const dateInput = this.element.querySelector("[data-flatpickr-type-value=\"datetime\"]");
    if (dateInput?.dataset.pendingScheduledAt) return true;
    if (dateInput && dateInput.value !== dateInput.dataset.unsavedChangesOriginalValue) {
      return true;
    }

    const locationInput = this.element.querySelector("[data-places-target=\"address\"]");
    if (locationInput && locationInput.value !== locationInput.dataset.originalValue) {
      return true;
    }

    const gifPickerElement = this.element.querySelector("[data-controller~=\"gifpicker\"]");
    const gifPicker = gifPickerElement &&
      this.application.getControllerForElementAndIdentifier(gifPickerElement, "gifpicker");
    return (gifPicker?.selectedGifs?.length || 0) > 0;
  }

  discardUncommittedModalChanges() {
    delete this.element.dataset.unsavedChangesModalPending;

    const dateInput = this.element.querySelector("[data-flatpickr-type-value=\"datetime\"]");
    if (dateInput) {
      delete dateInput.dataset.pendingScheduledAt;
      delete dateInput.dataset.pendingScheduleDisplay;
    }

    const locationInput = this.element.querySelector("[data-places-target=\"address\"]");
    if (locationInput) {
      locationInput.value = locationInput.dataset.originalValue || "";
      delete locationInput.dataset.selectedValue;
      locationInput.setCustomValidity("");
    }

    const gifPickerElement = this.element.querySelector("[data-controller~=\"gifpicker\"]");
    const gifPicker = gifPickerElement &&
      this.application.getControllerForElementAndIdentifier(gifPickerElement, "gifpicker");
    if (gifPicker) {
      gifPicker.resetSelection();
      gifPicker.renderSelectionPreview();
      gifPicker.showCountDown();
    }
  }

  markClean() {
    this.initialState = this.serialize();
    this.element.dataset.unsavedChangesBaseline = this.initialState;
    this.allowNavigation = false;
  }

  async discard() {
    const baseline = JSON.parse(this.initialState);
    const pondRoots = this.element.querySelectorAll(".filepond--root");
    if (pondRoots.length) {
      const FilePond = await loadFilePond();
      this.FilePond = FilePond;
      await Promise.all(Array.from(pondRoots).map(async root => {
        const pond = FilePond.find(root);
        if (!pond) return;
        // Discard newly selected files without deleting previously saved media.
        const drafts = pond.getFiles().filter(file =>
          file.origin === FilePond.FileOrigin.INPUT || file.origin === FilePond.FileOrigin.LIMBO
        );
        if (!drafts.length) return;

        // Discard was already confirmed. Don't open a second removal dialog.
        const beforeRemoveFile = pond.beforeRemoveFile;
        pond.beforeRemoveFile = null;
        try {
          // removeFiles(ids) returns an array of promises in this FilePond version.
          await Promise.all(drafts.map(file => pond.removeFile(file.id, { revert: false, remove: false })));
        } finally {
          pond.beforeRemoveFile = beforeRemoveFile;
        }
      }));
    }

    this.element.reset();
    const editors = this.element.querySelectorAll("[data-controller~='quill'], [contenteditable='true']:not(.ql-editor)");
    for (const [index, wrapper] of Array.from(editors).entries()) {
      const controller = this.application.getControllerForElementAndIdentifier(wrapper, "quill");
      const html = baseline.editors[index] || "";
      if (controller?.quill) {
        controller.clear();
        if (html) controller.quill.clipboard.dangerouslyPasteHTML(html);
      } else {
        (wrapper.querySelector(".ql-editor") || wrapper).innerHTML = html;
      }
    }
    // Let editor/upload listeners settle before Turbo captures the clean form.
    await new Promise(resolve => requestAnimationFrame(resolve));
    this.markClean();
  }

  isDirty() {
    return !this.allowNavigation && !this.submitting && this.serialize() !== this.initialState;
  }

  serialize() {
    const fields = Array.from(this.element.elements || []).filter(field =>
      field.name && !["hidden", "submit", "button", "reset"].includes(field.type) &&
      (field.type !== "file" || field.files?.length) &&
      !field.closest("[data-unsaved-changes-ignore], .filepond--root")
    ).map(field => {
      if (field.type === "file") {
        return [field.name, Array.from(field.files || []).map(file => [file.name, file.size, file.lastModified])];
      }
      if (["checkbox", "radio"].includes(field.type)) return [field.name, field.value, field.checked];
      if (field.multiple) return [field.name, Array.from(field.selectedOptions).map(option => option.value)];
      return [field.name, field.value];
    });
    const editors = Array.from(this.element.querySelectorAll("[data-controller~='quill'], [contenteditable='true']:not(.ql-editor)")).map(wrapper => {
      const editor = wrapper.querySelector(".ql-editor") || wrapper;
      return editor.textContent.trim() || editor.querySelector("img, video, iframe") ? editor.innerHTML.trim() : "";
    });
    // Removed preview nodes remain during FilePond's exit animation. They are
    // not draft files and must not become part of the new clean baseline.
    const attachments = Array.from(this.element.querySelectorAll(".filepond--root")).flatMap(root => {
      const pond = this.FilePond?.find(root);
      if (pond) return pond.getFiles().map(file => file.id);
      return Array.from(root.querySelectorAll(".filepond--item"))
        .map(item => item.id.replace(/^filepond--item-/, ""));
    });
    return JSON.stringify({ fields, editors, attachments });
  }
}
